import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ActionButton,
  BoldText,
  CardContainer,
  CardModalities,
  CategoriesRow,
  CategoryBadge,
  Container,
  ContentSection,
  DescriptionText,
  FuzzyBotSection,
  HeaderContent,
  HeaderMainContent,
  HeaderRow,
  ImageSection,
  ImageWrapper,
  MainContent,
  MainTitle,
  RobotImagePlaceholder,
  Section,
  SectionBot,
  SubTitle,
  SubTitlePage,
  TaskItem,
  TaskNumber,
  TasksList,
  TaskText,
  TitleGroup,
  TitleWrapper,
} from './styles';
import {
  FilePdfIcon,
  GridLayoutIcon,
  PalleteColorIcon,
  TrophyIcon,
  RulerAndPenIcon,
  MiniBotIcon,
} from '../../../assets/svg/SVGComponents';
import { RulesDownloadCard } from '../../../components/Tournament';
import { TOURNAMENT_RULES_PDF_URL } from '../../../config/tournament';
import SectionTutorialButton from '../../../components/Onboarding/SectionTutorialButton';
import PrimaryButton from '../../../components/Buttons/PrimaryButton';
import CreateTournamentButton, {
  TournamentSchoolNotice,
} from '../../../components/Buttons/CreateTournamentButton';
import { useTranslation } from 'react-i18next';
import { TutorialId } from '../../../hooks/onboarding';
import TecnologyEraCard, {
  IEraVideoItem,
} from '../../../components/TecnologyEraCard';

const TournamentsBriefing: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const taskListItemsPainter = [
    {
      id: 1,
      era: 'Era medieval',
      description: 'Trajetória de uma bola de canhão (Parábola).',
    },
    {
      id: 2,
      era: '1ª Revolução Industrial',
      description: 'Trilhos da máquina a vapor (Retas).',
    },
    {
      id: 3,
      era: '2ª Revolução Industrial',
      description: 'Filamento da lâmpada (Zig-zag).',
    },
    {
      id: 4,
      era: '3ª Revolução Industrial',
      description: 'Tela de um computador (Quadrado).',
    },
    {
      id: 5,
      era: '4ª Revolução Industrial',
      description: 'Rosto de um robô (Círculo).',
    },
  ];

  const taskListItemsFollower = [
    {
      id: 1,
      era: 'Era medieval',
      description: 'A Ponte Mecânica.',
    },
    {
      id: 2,
      era: '1ª Revolução Industrial',
      description: 'A Engrenagem Perdida.',
    },
    {
      id: 3,
      era: '2ª Revolução Industrial',
      description: 'Transmitindo Energia.',
    },
    {
      id: 4,
      era: '3ª Revolução Industrial',
      description: 'Elevador Automatizado.',
    },
    {
      id: 5,
      era: '4ª Revolução Industrial',
      description: 'Leitura dos Símbolos.',
    },
  ];

  const eraVideoItemsMock: IEraVideoItem[] = [
    {
      index: 0,
      videoUrl: 'https://youtube.com/shorts/NnHypKsOQgo?feature=share',
      label: 'Era Medieval',
      image:
        'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/illustration+clean/Vector.png',
    },
    {
      index: 1,
      videoUrl: 'https://youtube.com/shorts/0ZW35TOgRxc?feature=share',
      label: '1º Revolução Industrial',
      image:
        'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/illustration+clean/1+rev.png',
    },
    {
      index: 2,
      videoUrl: 'https://youtube.com/shorts/oL4p6zwg0tI?feature=share',
      label: '2º Revolução Industrial',
      image:
        'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/illustration+clean/2+rev.png',
    },
    {
      index: 3,
      videoUrl: 'https://youtube.com/shorts/DxKsIUfJJRc?feature=share',
      label: '3º Revolução Industrial',
      image:
        'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/illustration+clean/3+rev.png',
    },
    {
      index: 4,
      videoUrl: 'https://youtube.com/shorts/2Fi9PH8U0zk?feature=share',
      label: '4º Revolução Industrial',
      image:
        'https://fuzzyplaydev.s3.us-east-2.amazonaws.com/illustration+clean/4+rev.png',
    },
  ];

  return (
    <Container>
      <HeaderContent>
        <HeaderMainContent>
          <TitleWrapper>
            <TrophyIcon
              width="2rem"
              stroke="#FF9800"
              strokeWidth={3}
              fill="none"
            />
            <h1>{t('h1.tournaments')}</h1>
            <SectionTutorialButton tutorialId={TutorialId.TOOLS} />
          </TitleWrapper>

          <CreateTournamentButton id="unlock-hardware-button" />
        </HeaderMainContent>
      </HeaderContent>

      <TournamentSchoolNotice />

      <MainContent>
        <Section>
          <SubTitlePage>
            <PalleteColorIcon
              width="2rem"
              stroke="#FF9800"
              strokeWidth={2}
              fill="none"
            />
            <h2>Eras Tecnológicas</h2>
          </SubTitlePage>
          <ImageSection>
            <TecnologyEraCard eras={eraVideoItemsMock} />
          </ImageSection>
        </Section>

        <Section>
          <SubTitlePage>
            <GridLayoutIcon
              width="2rem"
              stroke="#FF9800"
              strokeWidth={2}
              fill="none"
            />
            <h2>Modalidades & Categorias</h2>
          </SubTitlePage>
          <CardModalities>
            <CardContainer>
              <ImageSection>
                {/* Pass your actual image asset to the src below */}
                <RobotImagePlaceholder
                  src="https://fuzzyplaydev.s3.us-east-2.amazonaws.com/illustration+clean/Group+14045%402x.png"
                  alt="Robot painter illustration"
                />
              </ImageSection>

              <ContentSection>
                <HeaderRow>
                  <TitleGroup>
                    <MainTitle>Robô Pintor</MainTitle>
                    <SubTitle>Fundamental I</SubTitle>
                  </TitleGroup>
                  <ActionButton>Controlado Remotamente</ActionButton>
                </HeaderRow>

                <DescriptionText>
                  Os robôs são controlados remotamente para desenhar formas
                  sobre a arena, em 5 ambientes correspondentes a diferentes
                  eras tecnológicas.
                </DescriptionText>

                <CategoriesRow>
                  <CategoryBadge>
                    <span>
                      <MiniBotIcon
                        width="1.4rem"
                        strokeWidth={2}
                        stroke="#1e88e1"
                      />
                    </span>
                    <div>
                      <h4>Categoria I</h4>
                      <p>1º, 2º e 3º anos</p>
                    </div>
                  </CategoryBadge>
                  <CategoryBadge>
                    <span>
                      <MiniBotIcon
                        width="1.4rem"
                        strokeWidth={2}
                        stroke="#1e88e1"
                      />
                    </span>
                    <div>
                      <h4>Categoria II</h4>
                      <p>4º e 5º anos</p>
                    </div>
                  </CategoryBadge>
                </CategoriesRow>

                <TasksList>
                  {taskListItemsPainter.map((task, index) => (
                    <TaskItem key={index}>
                      <TaskNumber>{task.id}</TaskNumber>
                      <TaskText>
                        <BoldText>{task.era}</BoldText> - {task.description}
                      </TaskText>
                    </TaskItem>
                  ))}
                </TasksList>
              </ContentSection>
            </CardContainer>
            <CardContainer>
              <ImageSection>
                {/* Pass your actual image asset to the src below */}
                <RobotImagePlaceholder
                  src="https://fuzzyplaydev.s3.us-east-2.amazonaws.com/illustration+clean/illustration-seguidor+de+trilha-2x.png"
                  alt="Robot painter illustration"
                />
              </ImageSection>

              <ContentSection>
                <HeaderRow>
                  <TitleGroup>
                    <MainTitle>Seguidor de linha</MainTitle>
                    <SubTitle>Ensino fundamental II</SubTitle>
                  </TitleGroup>
                  <ActionButton>Autônomo com IA</ActionButton>
                </HeaderRow>

                <DescriptionText>
                  Os robôs atuam de forma autônoma, seguindo linhas do mapa e
                  usando câmera com IA para navegação adaptativa. Cumprem 5
                  missões mecânicas e lógicas.
                </DescriptionText>

                <CategoriesRow>
                  <CategoryBadge>
                    <span>
                      <MiniBotIcon
                        width="1.4rem"
                        strokeWidth={2}
                        stroke="#1e88e1"
                      />
                    </span>
                    <div>
                      <h4>Categoria III</h4>
                      <p>6º e 7º anos</p>
                    </div>
                  </CategoryBadge>
                  <CategoryBadge>
                    <span>
                      <MiniBotIcon
                        width="1.4rem"
                        strokeWidth={2}
                        stroke="#1e88e1"
                      />
                    </span>
                    <div>
                      <h4>Categoria IV</h4>
                      <p>8º e 9º anos</p>
                    </div>
                  </CategoryBadge>
                </CategoriesRow>

                <TasksList>
                  {taskListItemsFollower.map((task, index) => (
                    <TaskItem key={index}>
                      <TaskNumber>{task.id}</TaskNumber>
                      <TaskText>
                        <BoldText>{task.era}</BoldText> - {task.description}
                      </TaskText>
                    </TaskItem>
                  ))}
                </TasksList>
              </ContentSection>
            </CardContainer>
          </CardModalities>
        </Section>
        <Section>
          <SubTitlePage>
            <FilePdfIcon
              width="2rem"
              stroke="#FF9800"
              strokeWidth={1.75}
              fill="none"
            />
            <h2>Regulamento</h2>
          </SubTitlePage>
          <RulesDownloadCard
            href={TOURNAMENT_RULES_PDF_URL}
            title="Regulamento FRL 2026"
          />
        </Section>
        <Section>
          <SubTitlePage>
            <RulerAndPenIcon
              width="2rem"
              stroke="#FF9800"
              strokeWidth={2}
              fill="none"
            />
            <h2>Fuzzy Bot Ferramenta Oficial da FRL</h2>
          </SubTitlePage>
          <SectionBot>
            <FuzzyBotSection>
              <h3>
                A ferramenta pedagógica oficial da Fuzzy Robotics League,
                <br /> criado pela Fuzzy Makers com foco na democratização de
                <br />
                torneios de robótica no Brasil.
              </h3>
              <PrimaryButton
                type="button"
                onClick={() => navigate('/hardwares/profile/fuzzy_bot')}
              >
                <RulerAndPenIcon
                  width="1.6rem"
                  stroke="#ffff"
                  strokeWidth={3}
                  fill="none"
                />
                Acesse as trilhas do projetos
              </PrimaryButton>
            </FuzzyBotSection>
            <ImageWrapper>
              <div>
                <img
                  src="https://fuzzyplaydev.s3.us-east-2.amazonaws.com/illustration+clean/BG-FRL-FZ-BOT.png"
                  alt="backgorund"
                />
              </div>
            </ImageWrapper>
          </SectionBot>
        </Section>
      </MainContent>
    </Container>
  );
};

export default TournamentsBriefing;
